import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
  useTheme,
  IconButton,
  MenuItem,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { Delete } from '@mui/icons-material';
import axios from 'axios';
import LoadingSpinner from '../../_GlobalComponents/LoadingSpinner';
import AddItemModal from './AddItemModal';
import DeleteItemModal from './DeleteItemModal';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RowDetailModal from './SuggestedOrderTable/Components/RowDetailModal';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { resolveCustomerToken } from '../utils/customerToken';
import ItemImagePreviewLink from './ItemImagePreviewLink';

const TOP_FACET_KEYS = ['top_facet_1', 'top_facet_2', 'top_facet_3', 'top_facet_4', 'top_facet_5'];
const ALPHANUMERIC_SORTER = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});
const AUTO_GENERATE_ITEM_TYPE_OPTIONS = [
  { label: 'All Item Types', value: '0' },
  { label: 'Accessories', value: '3' },
  { label: 'Ammunition', value: '4' },
  { label: 'Archery', value: '6' },
  { label: 'Handguns', value: '1' },
  { label: 'Long Guns', value: '2' },
  { label: 'NFA', value: '8' },
  { label: 'Optics', value: '5' },
  { label: 'Reloading', value: '7' },
];

const isNumericLeadingValue = (value) => /^\d/.test(String(value).trim());

const compareFacetValues = (a, b) => {
  const aNumericFirst = isNumericLeadingValue(a);
  const bNumericFirst = isNumericLeadingValue(b);

  if (aNumericFirst && !bNumericFirst) return -1;
  if (!aNumericFirst && bNumericFirst) return 1;

  return ALPHANUMERIC_SORTER.compare(String(a), String(b));
};

const parseStructuredFaqContent = (value) => {
  if (!value) return null;
  if (typeof value === 'object') return value;
  if (typeof value !== 'string') return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const ResponsiveFacetFilters = ({
  leadingControl,
  hiddenFilter,
  setHiddenFilter,
  manufacturers,
  selectedManufacturer,
  setSelectedManufacturer,
  types,
  selectedType,
  setSelectedType,
  categories,
  selectedCategory,
  setSelectedCategory,
  facetNames,
  facetOptionsByName,
  selectedFacetFilters,
  setFacetFilter,
  onClearAllFilters,
  toolbarFieldSx,
}) => {
  const rootRef = useRef(null);
  const fixedControlsRef = useRef(null);
  const [maxVisibleFacetCount, setMaxVisibleFacetCount] = useState(0);

  const CONTROL_GAP_PX = 6;
  const FACET_CONTROL_WIDTH_PX = 200;
  const CLEAR_BUTTON_WIDTH_PX = 80;
  const hasActiveFilters =
    hiddenFilter !== 'all' ||
    selectedManufacturer !== 'All' ||
    selectedType !== 'All' ||
    selectedCategory !== 'All' ||
    Object.values(selectedFacetFilters).some(Boolean);

  useEffect(() => {
    const rootElement = rootRef.current;
    const fixedControlsElement = fixedControlsRef.current;

    if (!rootElement || !fixedControlsElement) {
      return undefined;
    }

    const recalculateVisibleFacetCount = () => {
      const rootWidth = rootElement.getBoundingClientRect().width;
      const fixedWidth = fixedControlsElement.getBoundingClientRect().width;
      const clearButtonWidth = hasActiveFilters ? CLEAR_BUTTON_WIDTH_PX : 0;
      const availableWidth = Math.max(
        0,
        rootWidth - fixedWidth - clearButtonWidth - CONTROL_GAP_PX * 2,
      );
      const slotWidth = FACET_CONTROL_WIDTH_PX + CONTROL_GAP_PX;
      const computedCount = Math.max(0, Math.floor(availableWidth / slotWidth));
      const hasFacetFilters =
        selectedCategory !== 'All' && selectedCategory !== '' && facetNames.length > 0;
      const nextCount = hasFacetFilters ? Math.max(1, computedCount) : computedCount;
      setMaxVisibleFacetCount(nextCount);
    };

    recalculateVisibleFacetCount();

    const resizeObserver = new ResizeObserver(() => {
      recalculateVisibleFacetCount();
    });

    resizeObserver.observe(rootElement);
    resizeObserver.observe(fixedControlsElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [facetNames.length, hasActiveFilters, selectedCategory]);

  const orderedFacetNames = useMemo(() => {
    const selectedNames = facetNames.filter((name) => Boolean(selectedFacetFilters[name]));
    const unselectedNames = facetNames.filter((name) => !selectedFacetFilters[name]);
    return [...selectedNames, ...unselectedNames];
  }, [facetNames, selectedFacetFilters]);

  const visibleFacetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }
    return orderedFacetNames.slice(0, maxVisibleFacetCount);
  }, [maxVisibleFacetCount, orderedFacetNames, selectedCategory]);

  return (
    <Box
      ref={rootRef}
      display='flex'
      gap={0.75}
      alignItems='flex-end'
      flexWrap='nowrap'
      sx={{ width: '100%', overflow: 'hidden' }}
    >
      <Box ref={fixedControlsRef} display='flex' gap={0.75} alignItems='flex-end' flexShrink={0}>
        {leadingControl}
        <Autocomplete
          sx={{
            minWidth: 140,
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'white',
            },
          }}
          options={[
            { label: 'All Items', value: 'all' },
            { label: 'Visible Only', value: 'visible' },
            { label: 'Hidden Only', value: 'hidden' },
          ]}
          value={
            hiddenFilter === 'hidden'
              ? { label: 'Hidden Only', value: 'hidden' }
              : hiddenFilter === 'visible'
                ? { label: 'Visible Only', value: 'visible' }
                : { label: 'All Items', value: 'all' }
          }
          isOptionEqualToValue={(option, value) => option.value === value.value}
          getOptionLabel={(option) => option.label}
          onChange={(_, newValue) => setHiddenFilter(newValue?.value || 'all')}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Visibility' />}
          clearOnBlur={false}
          disableClearable
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={manufacturers}
          value={selectedManufacturer === 'All' ? null : selectedManufacturer}
          onChange={(_, newValue) => setSelectedManufacturer(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Brand' />}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={types}
          value={selectedType === 'All' ? null : selectedType}
          onChange={(_, newValue) => setSelectedType(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Item Type' />}
          clearOnBlur={false}
        />
        <Autocomplete
          sx={toolbarFieldSx}
          options={categories}
          value={selectedCategory === 'All' ? null : selectedCategory}
          onChange={(_, newValue) => setSelectedCategory(!newValue ? 'All' : newValue)}
          renderInput={(params) => <TextField {...params} size='small' placeholder='Category' />}
          clearOnBlur={false}
        />
      </Box>

      <Box display='flex' gap={0.75} alignItems='flex-end' sx={{ minWidth: 0, overflow: 'hidden' }}>
        {visibleFacetNames.map((facetName) => (
          <Autocomplete
            key={facetName}
            sx={toolbarFieldSx}
            options={facetOptionsByName[facetName] || []}
            value={selectedFacetFilters[facetName] || null}
            onChange={(_, newValue) => setFacetFilter(facetName, newValue || '')}
            renderInput={(params) => <TextField {...params} size='small' placeholder={facetName} />}
            clearOnBlur={false}
          />
        ))}
      </Box>

      {hasActiveFilters && (
        <Button
          size='small'
          aria-label='Clear all filters'
          onClick={onClearAllFilters}
          sx={{
            flexShrink: 0,
            minWidth: 'auto',
            px: 0.5,
            color: 'common.white',
            textTransform: 'none',
            fontSize: '0.72rem',
            lineHeight: 1.05,
            textDecoration: 'underline',
            whiteSpace: 'pre-line',
            textAlign: 'center',
            '&:hover': {
              backgroundColor: 'transparent',
              textDecoration: 'underline',
            },
          }}
        >
          {'Clear All\nFilters'}
        </Button>
      )}
    </Box>
  );
};

const SettingsTable = ({ onOpenHelp }) => {
  const theme = useTheme();
  const autoGeneratedIconColor = theme.palette.primary.main;
  const [minMaxData, setMinMaxData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageColor, setMessageColor] = useState('#000');
  const [userPersonalization, setUserPersonalization] = useState(null);
  const [token, setToken] = useState('');
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [deleteItemModalOpen, setDeleteItemModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedItemNumber, setSelectedItemNumber] = useState(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [isQuickSearchOpen, setIsQuickSearchOpen] = useState(false);
  const [filterModel, setFilterModel] = useState({ items: [], quickFilterValues: [] });
  const [selectedManufacturer, setSelectedManufacturer] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [hiddenFilter, setHiddenFilter] = useState('all');
  const [selectedFacetFilters, setSelectedFacetFilters] = useState({});
  const [autoGenerateModalOpen, setAutoGenerateModalOpen] = useState(false);
  const [autoGenerateFaq, setAutoGenerateFaq] = useState(null);
  const [autoGenerateFaqLoading, setAutoGenerateFaqLoading] = useState(false);
  const [autoGenerateFaqError, setAutoGenerateFaqError] = useState('');
  const [autoGenerateCount, setAutoGenerateCount] = useState('100');
  const [autoGenerateItemType, setAutoGenerateItemType] = useState('0');
  const [autoGenerateSubmitting, setAutoGenerateSubmitting] = useState(false);
  const [autoGenerateSubmitError, setAutoGenerateSubmitError] = useState('');
  const [autoGenerateStep, setAutoGenerateStep] = useState('configure');
  const quickSearchRef = useRef(null);
  const hasQuickFilterValueRef = useRef(false);

  const fetchMinMaxData = useCallback(async (resolvedToken) => {
    const response = await axios.post('/suggestedOrders/getMinMax', {
      customerNumber: resolvedToken,
    });

    const { result, message, message_color, userId } = response.data;
    setMessage(message);
    setMessageColor(message_color || '#000');

    if (result === 'success') {
      setMinMaxData(response.data.details);
    }

    return userId;
  }, []);

  useEffect(() => {
    const resolvedToken = resolveCustomerToken();
    setToken(resolvedToken);
    setIsLoading(true);

    fetchMinMaxData(resolvedToken)
      .then((userId) => {
        if (!userId) {
          return;
        }
        axios
          .post('/userPersonalization/getbyroute', {
            domain: 'TSW',
            route: 'suggested-orders',
            userId,
          })
          .then((userResponse) => {
            setUserPersonalization(userResponse.data);
          })
          .catch((error) => {
            console.error('Error fetching user personalization:', error);
          });
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [fetchMinMaxData]);

  useEffect(() => {
    hasQuickFilterValueRef.current = Boolean(filterModel.quickFilterValues?.length);
  }, [filterModel.quickFilterValues]);

  const buttonStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.tsw.tertiary,
      fontSize: '1rem',
      fontWeight: 600,
      whiteSpace: 'pre',
      mr: 2,
    }),
    [theme.palette.tsw.tertiary],
  );

  const handleAddUpdateItem = useCallback(
    (itemNumber, itemMin, itemMax) => {
      return axios.post('/suggestedOrders/addUpdateItem', {
        customerNumber: token,
        itemNumber,
        itemMin,
        itemMax,
      });
    },
    [token],
  );

  const isItemHidden = useCallback((value) => {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return (
        normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'y'
      );
    }
    return false;
  }, []);

  const handleUnhideItem = useCallback(
    async (row) => {
      try {
        await axios.post('/suggestedOrders/addUpdateItem', {
          customerNumber: token,
          itemNumber: row.item_number,
          itemMin: parseFloat(row.min_quantity) || 0,
          itemMax: parseFloat(row.max_quantity) || 0,
          h: false,
        });

        setMinMaxData((prev) =>
          prev.map((item) =>
            item.item_number === row.item_number ? { ...item, is_hidden: 'false' } : item,
          ),
        );
      } catch (error) {
        console.error('Error unhiding item:', error);
      }
    },
    [token],
  );

  const handleItemOpenModal = (itemNumber) => {
    setSelectedItemNumber(itemNumber);
    setModalOpen(true);
  };

  const handleItemCloseModal = () => {
    setModalOpen(false);
    setSelectedItemNumber(null);
  };

  const RenderNumericField = ({ value, row, field }) => {
    const [inputValue, setInputValue] = useState(value);
    const [error, setError] = useState(false);
    const [status, setStatus] = useState('idle');

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleInputChange = async (e) => {
      const newValue = e.target.value;
      setInputValue(newValue);
      if (newValue === '' || isNaN(newValue)) return;

      const parsedValue = parseFloat(newValue);
      const itemNumber = row.item_number;
      const currentItem = minMaxData.find((item) => item.item_number === itemNumber) || row;
      const itemMin =
        field === 'min_quantity' ? parsedValue : parseFloat(currentItem.min_quantity) || 0;
      const itemMax =
        field === 'max_quantity' ? parsedValue : parseFloat(currentItem.max_quantity) || 0;

      if (field === 'max_quantity' && itemMin > parsedValue) {
        setError(true);
      } else if (field === 'min_quantity' && parsedValue > itemMax) {
        setError(true);
      } else {
        setError(false);
        setStatus('loading');
        setMinMaxData((prev) =>
          prev.map((item) =>
            item.item_number === itemNumber
              ? {
                  ...item,
                  [field]: parsedValue,
                }
              : item,
          ),
        );

        try {
          await handleAddUpdateItem(itemNumber, itemMin, itemMax);
          setStatus('success');
          setTimeout(() => setStatus('idle'), 1000);
        } catch {
          setStatus('error');
        }
      }
    };

    return (
      <Box display='flex' alignItems='center' gap={0.5}>
        <TextField
          variant='outlined'
          size='small'
          value={inputValue}
          onChange={handleInputChange}
          error={error}
          helperText={error ? 'Invalid value' : ''}
          inputProps={{
            style: { width: '52px', textAlign: 'center' },
            type: 'number',
          }}
        />
        {status === 'loading' && <CircularProgress size={16} />}
        {status === 'success' && <CheckCircleIcon color='success' />}
        {status === 'error' && <ErrorIcon color='error' />}
      </Box>
    );
  };

  const deleteItem = (itemNumber) => {
    setLoading(true);
    axios
      .post('/suggestedOrders/deleteItem', {
        customerNumber: token,
        itemNumber,
      })
      .then((response) => {
        handleDeleteItemModalClose();
        setMinMaxData((prev) => prev.filter((item) => item.item_number !== itemNumber));
        console.log('Delete successful:', response);
      })
      .catch((error) => {
        console.error('Error deleting item:', error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleAddItemModalClose = () => {
    setAddItemModalOpen(false);
  };

  const handleMinMaxItemsChanged = useCallback(async () => {
    if (!token) {
      return;
    }

    try {
      await fetchMinMaxData(token);
    } catch (error) {
      console.error('Error refreshing min/max data:', error);
    }
  }, [fetchMinMaxData, token]);

  const handleDeleteItemModalClose = () => {
    setDeleteItemModalOpen(false);
    setItemToDelete(null);
  };

  const normalizeFacetEntry = useCallback((entry) => {
    if (!entry) return null;
    if (typeof entry === 'object') {
      const [facetName, facetValue] = Object.entries(entry)[0] || [];
      if (!facetName) return null;
      const valueText =
        facetValue === null || facetValue === undefined ? '' : String(facetValue).trim();
      return {
        name: String(facetName).trim(),
        value: valueText,
      };
    }
    return null;
  }, []);

  const getRowFacetPairs = useCallback(
    (row) =>
      TOP_FACET_KEYS.map((key) => normalizeFacetEntry(row?.[key])).filter((value) =>
        Boolean(value?.name),
      ),
    [normalizeFacetEntry],
  );

  const getRowFacetMap = useCallback(
    (row) =>
      getRowFacetPairs(row).reduce((acc, facet) => {
        acc[facet.name] = facet.value;
        return acc;
      }, {}),
    [getRowFacetPairs],
  );

  const manufacturers = useMemo(() => {
    const filtered =
      selectedType === 'All' || selectedType === ''
        ? minMaxData
        : minMaxData.filter((item) => item.item_type === selectedType);
    return Array.from(
      new Set(filtered.map((item) => item.manufacturer_name).filter(Boolean)),
    ).sort();
  }, [minMaxData, selectedType]);

  const types = useMemo(() => {
    const filtered =
      selectedManufacturer === 'All' || selectedManufacturer === ''
        ? minMaxData
        : minMaxData.filter((item) => item.manufacturer_name === selectedManufacturer);
    return Array.from(new Set(filtered.map((item) => item.item_type).filter(Boolean))).sort();
  }, [minMaxData, selectedManufacturer]);

  const categories = useMemo(() => {
    const filtered = minMaxData.filter((item) => {
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      return manufacturerMatches && typeMatches;
    });
    return Array.from(new Set(filtered.map((item) => item.category).filter(Boolean))).sort();
  }, [minMaxData, selectedManufacturer, selectedType]);

  const baseFilteredRows = useMemo(() => {
    return minMaxData.filter((item) => {
      const isHidden = isItemHidden(item.is_hidden);
      const hiddenMatches =
        hiddenFilter === 'all' ||
        (hiddenFilter === 'hidden' && isHidden) ||
        (hiddenFilter === 'visible' && !isHidden);
      const manufacturerMatches =
        selectedManufacturer === 'All' ||
        selectedManufacturer === '' ||
        item.manufacturer_name === selectedManufacturer;
      const typeMatches =
        selectedType === 'All' || selectedType === '' || item.item_type === selectedType;
      const categoryMatches =
        selectedCategory === 'All' || selectedCategory === '' || item.category === selectedCategory;
      return hiddenMatches && manufacturerMatches && typeMatches && categoryMatches;
    });
  }, [
    hiddenFilter,
    isItemHidden,
    minMaxData,
    selectedCategory,
    selectedManufacturer,
    selectedType,
  ]);

  const facetNames = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '') {
      return [];
    }

    const seen = new Set();
    const orderedNames = [];
    const categoryItems = minMaxData.filter((item) => item.category === selectedCategory);

    categoryItems.forEach((item) => {
      TOP_FACET_KEYS.forEach((key) => {
        const facet = normalizeFacetEntry(item?.[key]);
        if (facet?.name && !seen.has(facet.name)) {
          seen.add(facet.name);
          orderedNames.push(facet.name);
        }
      });
    });

    return orderedNames;
  }, [minMaxData, normalizeFacetEntry, selectedCategory]);

  const facetOptionsByName = useMemo(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return {};
    }

    return facetNames.reduce((acc, facetName) => {
      const rowsMatchingOtherFacetSelections = baseFilteredRows.filter((row) => {
        const facetMap = getRowFacetMap(row);
        return facetNames.every((name) => {
          if (name === facetName) return true;
          const selectedValue = selectedFacetFilters[name];
          if (!selectedValue) return true;
          return facetMap[name] === selectedValue;
        });
      });

      const options = Array.from(
        new Set(
          rowsMatchingOtherFacetSelections
            .map((row) => getRowFacetMap(row)[facetName])
            .filter(Boolean),
        ),
      ).sort(compareFacetValues);

      acc[facetName] = options;
      return acc;
    }, {});
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedCategory, selectedFacetFilters]);

  useEffect(() => {
    if (!manufacturers.length) return;
    if (selectedManufacturer !== 'All' && !manufacturers.includes(selectedManufacturer)) {
      setSelectedManufacturer('All');
    }
  }, [manufacturers, selectedManufacturer]);

  useEffect(() => {
    if (!types.length) return;
    if (selectedType !== 'All' && !types.includes(selectedType)) {
      setSelectedType('All');
    }
  }, [selectedType, types]);

  useEffect(() => {
    if (!categories.length) return;
    if (selectedCategory !== 'All' && !categories.includes(selectedCategory)) {
      setSelectedCategory('All');
    }
  }, [categories, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      setSelectedFacetFilters({});
      return;
    }

    setSelectedFacetFilters((prev) => {
      const next = {};
      facetNames.forEach((name) => {
        if (prev[name]) {
          next[name] = prev[name];
        }
      });
      return next;
    });
  }, [facetNames, selectedCategory]);

  useEffect(() => {
    if (selectedCategory === 'All' || selectedCategory === '' || !facetNames.length) {
      return;
    }

    setSelectedFacetFilters((prev) => {
      let changed = false;
      const next = { ...prev };

      facetNames.forEach((name) => {
        const selectedValue = next[name];
        if (!selectedValue) return;
        const options = facetOptionsByName[name] || [];
        if (!options.includes(selectedValue)) {
          delete next[name];
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [facetNames, facetOptionsByName, selectedCategory]);

  const setFacetFilter = useCallback((facetName, value) => {
    setSelectedFacetFilters((prev) => {
      if (!value) {
        if (!prev[facetName]) return prev;
        const next = { ...prev };
        delete next[facetName];
        return next;
      }
      if (prev[facetName] === value) return prev;
      return {
        ...prev,
        [facetName]: value,
      };
    });
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setHiddenFilter('all');
    setSelectedManufacturer('All');
    setSelectedType('All');
    setSelectedCategory('All');
    setSelectedFacetFilters({});
    setFilterModel({ items: [], quickFilterValues: [] });
  }, []);

  const openAutoGenerateModal = useCallback(() => {
    setAutoGenerateModalOpen(true);
    setAutoGenerateSubmitError('');
    setAutoGenerateStep('configure');
  }, []);

  const closeAutoGenerateModal = useCallback(() => {
    if (autoGenerateSubmitting) {
      return;
    }
    setAutoGenerateModalOpen(false);
    setAutoGenerateStep('configure');
  }, [autoGenerateSubmitting]);

  useEffect(() => {
    if (!autoGenerateModalOpen) {
      return;
    }

    const loadAutoGenerateFaq = async () => {
      setAutoGenerateFaqLoading(true);
      setAutoGenerateFaqError('');
      try {
        const response = await axios.get('/suggestedOrders/faqs');
        const faqData = Array.isArray(response.data) ? response.data : response.data?.faqs || [];
        const targetFaq = faqData.find((faq) => Number(faq.faq_id) === 6) || null;
        setAutoGenerateFaq(targetFaq);
      } catch (error) {
        console.error('Error loading auto-generate FAQ:', error);
        setAutoGenerateFaq(null);
        setAutoGenerateFaqError('Unable to load FAQ content right now.');
      } finally {
        setAutoGenerateFaqLoading(false);
      }
    };

    loadAutoGenerateFaq();
  }, [autoGenerateModalOpen]);

  const handleAutoGenerateNext = useCallback(() => {
    setAutoGenerateSubmitError('');
    setAutoGenerateStep('confirm');
  }, []);

  const handleAutoGenerateCancel = useCallback(() => {
    if (autoGenerateSubmitting) {
      return;
    }

    if (autoGenerateStep === 'confirm') {
      setAutoGenerateSubmitError('');
      setAutoGenerateStep('configure');
      return;
    }

    closeAutoGenerateModal();
  }, [autoGenerateStep, autoGenerateSubmitting, closeAutoGenerateModal]);

  const handleAutoGenerateConfirm = useCallback(async () => {
    if (!token || autoGenerateSubmitting) {
      return;
    }

    setAutoGenerateSubmitting(true);
    setAutoGenerateSubmitError('');

    try {
      const generateResponse = await axios.post('/suggestedOrders/generateMinMax', {
        customerNumber: token,
        i: Number(autoGenerateItemType),
        limit: Number(autoGenerateCount),
      });

      const generatedItems = generateResponse?.data?.details?.items;
      const items = Array.isArray(generatedItems) ? generatedItems : [];

      if (!items.length) {
        setAutoGenerateSubmitError('No items were returned to add.');
        return;
      }

      const addUpdateResults = await Promise.allSettled(
        items.map((item) =>
          handleAddUpdateItem(item.item_number, item.min_quantity, item.max_quantity),
        ),
      );

      const successfulAdds = addUpdateResults.filter((result) => result.status === 'fulfilled');
      if (successfulAdds.length === 0) {
        setAutoGenerateSubmitError('Unable to add generated items to Min / Max settings.');
        return;
      }

      await fetchMinMaxData(token);
      setAutoGenerateModalOpen(false);
      setAutoGenerateStep('configure');
    } catch (error) {
      console.error('Error running auto-generate min/max:', error);
      setAutoGenerateSubmitError('Failed to auto-generate min / max settings.');
    } finally {
      setAutoGenerateSubmitting(false);
    }
  }, [
    autoGenerateCount,
    autoGenerateItemType,
    autoGenerateSubmitting,
    fetchMinMaxData,
    handleAddUpdateItem,
    token,
  ]);

  const autoGenerateCurrentCount = minMaxData.length;
  const autoGenerateSelectedCount = Number(autoGenerateCount) || 0;
  const autoGenerateProjectedCount = autoGenerateCurrentCount + autoGenerateSelectedCount;

  const filteredData = useMemo(() => {
    return baseFilteredRows.filter((item) => {
      const rowFacetMap = getRowFacetMap(item);
      return facetNames.every((name) => {
        const selectedValue = selectedFacetFilters[name];
        if (!selectedValue) return true;
        return rowFacetMap[name] === selectedValue;
      });
    });
  }, [baseFilteredRows, facetNames, getRowFacetMap, selectedFacetFilters]);

  const renderHelpHeader = useCallback(
    (label, columnName) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.35,
          minWidth: 0,
          pr: 2.5,
        }}
      >
        <Tooltip title='What does this mean?'>
          <IconButton
            size='small'
            aria-label={`What does ${label} mean?`}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onOpenHelp?.({
                faqDescription: 'What do all the columns mean',
                sectionTitle: 'Smart Items Tab',
                columnName,
              });
            }}
            sx={{ p: 0.25 }}
          >
            <HelpOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Typography variant='body2' sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
          {label}
        </Typography>
      </Box>
    ),
    [onOpenHelp],
  );

  const getPersonalizedValue = useCallback(
    (key) => {
      const match = userPersonalization?.find((p) => p.personalized_element === key);
      return match?.personalized_value;
    },
    [userPersonalization],
  );

  const yourQtyHeader = useMemo(
    () => getPersonalizedValue('YourQtyHeader') || 'Your Qty',
    [getPersonalizedValue],
  );

  const toolbarFieldSx = useMemo(
    () => ({
      minWidth: 200,
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.common.white,
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[400],
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.grey[500],
      },
      '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
      },
    }),
    [theme],
  );

  const SettingsToolbar = useCallback(
    () => (
      <GridToolbarContainer
        sx={{
          px: 0.75,
          py: 0.75,
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: 0.75,
          backgroundColor: theme.palette.primary.main,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
          <ResponsiveFacetFilters
            leadingControl={
              <Button
                variant='contained'
                sx={{
                  ...buttonStyle,
                  mr: 0,
                  minHeight: 34,
                  fontSize: '0.85rem',
                  px: 1.25,
                  color: theme.palette.primary.main,
                  backgroundColor: theme.palette.common.white,
                  '&:hover': { backgroundColor: theme.palette.grey[100] },
                }}
                onClick={() => setAddItemModalOpen(true)}
              >
                Add New Item
              </Button>
            }
            hiddenFilter={hiddenFilter}
            setHiddenFilter={setHiddenFilter}
            manufacturers={manufacturers}
            selectedManufacturer={selectedManufacturer}
            setSelectedManufacturer={setSelectedManufacturer}
            types={types}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            categories={categories}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            facetNames={facetNames}
            facetOptionsByName={facetOptionsByName}
            selectedFacetFilters={selectedFacetFilters}
            setFacetFilter={setFacetFilter}
            onClearAllFilters={handleClearAllFilters}
            toolbarFieldSx={toolbarFieldSx}
          />
        </Box>

        <Box sx={{ position: 'relative', width: 260, height: 34, ml: 'auto' }}>
          <Box
            sx={{
              position: 'absolute',
              right: 36,
              top: 0,
              width: 220,
              transformOrigin: 'right center',
              transform: isQuickSearchOpen
                ? 'translateX(0) scaleX(1)'
                : 'translateX(10px) scaleX(0.85)',
              opacity: isQuickSearchOpen ? 1 : 0,
              pointerEvents: isQuickSearchOpen ? 'auto' : 'none',
              transition: 'transform 220ms ease, opacity 220ms ease',
            }}
          >
            <GridToolbarQuickFilter
              inputRef={quickSearchRef}
              debounceMs={300}
              placeholder='Search'
              onBlur={() => {
                if (!hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Escape' && !hasQuickFilterValueRef.current) {
                  setIsQuickSearchOpen(false);
                }
              }}
              InputProps={{ startAdornment: null }}
              sx={{
                width: 220,
                pb: 0,
                '& input': {
                  color: theme.palette.common.white,
                },
                '& input::placeholder': {
                  color: theme.palette.common.white,
                  opacity: 0.9,
                },
                '& .MuiInput-underline:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:hover:before': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiInput-underline:after': {
                  borderBottomColor: theme.palette.common.white,
                },
                '& .MuiSvgIcon-root': {
                  color: theme.palette.common.white,
                },
              }}
            />
          </Box>
          <IconButton
            aria-label='search'
            onClick={() => {
              if (!isQuickSearchOpen) {
                setIsQuickSearchOpen(true);
                setTimeout(() => {
                  quickSearchRef.current?.focus();
                }, 0);
                return;
              }
              if (!hasQuickFilterValueRef.current) {
                setIsQuickSearchOpen(false);
              }
            }}
            sx={{ position: 'absolute', right: 0, top: 0, color: theme.palette.common.white }}
          >
            <SearchIcon fontSize='small' />
          </IconButton>
        </Box>
      </GridToolbarContainer>
    ),
    [
      buttonStyle,
      categories,
      facetNames,
      facetOptionsByName,
      hiddenFilter,
      isQuickSearchOpen,
      manufacturers,
      selectedCategory,
      selectedFacetFilters,
      selectedManufacturer,
      selectedType,
      setHiddenFilter,
      setFacetFilter,
      handleClearAllFilters,
      toolbarFieldSx,
      types,
      theme.palette.common.white,
      theme.palette.grey,
      theme.palette.primary.main,
    ],
  );

  const columns = useMemo(
    () => [
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => (
          <Box display='flex' flexDirection='row' justifyContent='flex-start'>
            {isItemHidden(params.row.is_hidden) && (
              <Tooltip title='Unhide Item' placement='top' arrow aria-label='Unhide item'>
                <VisibilityOutlinedIcon
                  sx={{ color: theme.palette.primary.main, cursor: 'pointer', mr: 1 }}
                  onClick={() => handleUnhideItem(params.row)}
                />
              </Tooltip>
            )}
            <Tooltip title='Delete' aria-label='Delete item'>
              <Delete
                sx={{ color: theme.palette.delete.main, cursor: 'pointer' }}
                onClick={() => {
                  setItemToDelete(params.row.item_number);
                  setDeleteItemModalOpen(true);
                }}
              />
            </Tooltip>
          </Box>
        ),
      },
      {
        field: 'min_quantity',
        headerName: 'Min Qty',
        renderHeader: () => renderHelpHeader('Min Qty', 'Min Qty'),
        width: 118,
        renderCell: (params) => (
          <RenderNumericField value={params.value} row={params.row} field='min_quantity' />
        ),
      },
      {
        field: 'max_quantity',
        headerName: 'Max Qty',
        renderHeader: () => renderHelpHeader('Max Qty', 'Max Qty'),
        width: 118,
        renderCell: (params) => (
          <RenderNumericField value={params.value} row={params.row} field='max_quantity' />
        ),
      },
      {
        field: 'dealer_mtd_sold',
        headerName: 'YTD Sold',
        renderHeader: () => renderHelpHeader('YTD Sold', 'YTD Sold'),
        width: 130,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'dealer_quantity_on_hand',
        headerName: yourQtyHeader,
        renderHeader: () => renderHelpHeader(yourQtyHeader, 'Your Qty'),
        width: 120,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant='body1'>{params.value}</Typography>
          </Box>
        ),
      },
      {
        field: 'auto_added',
        headerName: 'Auto Generated?',
        renderHeader: () => renderHelpHeader('Auto Generated?', 'Auto Generated?'),
        width: 180,
        renderCell: (params) => {
          const isAutoAdded =
            params.value === 1 ||
            params.value === '1' ||
            params.value === true ||
            params.value === 'true';

          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {isAutoAdded ? (
                <AutoAwesomeIcon sx={{ fontSize: 18, color: autoGeneratedIconColor }} />
              ) : null}
            </Box>
          );
        },
      },
      {
        field: 'category',
        headerName: 'Category',
        renderHeader: () => renderHelpHeader('Category', 'Category'),
        width: 130,
      },
      {
        field: 'description',
        headerName: 'Description',
        renderHeader: () => renderHelpHeader('Description', 'Description'),
        minWidth: 360,
        flex: 1,
        renderCell: (params) => {
          const itemNumber = params.row.item_number;
          const upc = params.row.upc;
          const itemLabel = itemNumber ?? 'N/A';
          const upcLabel = upc ?? 'N/A';
          const isDiscontinued =
            params.row.discontinued === true ||
            String(params.row.discontinued || '').trim().toLowerCase() === 'true';

          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
              }}
            >
              <ItemImagePreviewLink
                itemNumber={itemNumber}
                description={params.value}
                onClick={() => handleItemOpenModal(itemNumber)}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant='body2' color='text.secondary'>
                  {`SS Item #: ${itemLabel}`}
                </Typography>
                {isDiscontinued ? (
                  <Typography
                    variant='body2'
                    sx={{ color: theme.palette.secondary.main, fontWeight: 700 }}
                  >
                    DISCONTINUED
                  </Typography>
                ) : null}
              </Box>
              <Typography variant='body2' color='text.secondary'>
                {`UPC #: ${upcLabel}`}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'item_number',
        headerName: 'SS Item #',
        width: 1,
        sortable: false,
      },
    ],
    [
      handleUnhideItem,
      isItemHidden,
      renderHelpHeader,
      autoGeneratedIconColor,
      theme.palette.delete.main,
      theme.palette.primary.main,
      yourQtyHeader,
    ],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        boxSizing: 'border-box',
        minWidth: 0,
        minHeight: 0,
        flexGrow: 1,
        p: 2,
        overflow: 'hidden',
      }}
    >
      <LoadingSpinner isLoading={isLoading} />

      <Box display='flex' flexDirection='column' gap={1} sx={{ mb: 2 }}>
        <Box display='flex' justifyContent='center' alignItems='baseline' flexWrap='wrap' gap={1}>
          <Typography variant='body1' sx={{ color: messageColor, fontSize: '1.1rem' }}>
            {message}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          alignSelf: 'flex-end',
          mb: 1.5,
          px: 1,
          py: 0.75,
          borderRadius: 1.5,
          border: `1px solid ${theme.palette.primary.main}30`,
          backgroundColor: `${theme.palette.primary.main}0d`,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: 1.5,
          width: 'fit-content',
          maxWidth: '100%',
        }}
      >
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: autoGeneratedIconColor }} />
          <Typography sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Auto-Generate based on my purchase history
          </Typography>
        </Box>
        <Button variant='contained' size='small' onClick={openAutoGenerateModal}>
          Start
        </Button>
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <DataGrid
          rows={filteredData}
          columns={columns}
          getRowId={(row) => row.item_number}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 88}
          disableRowSelectionOnClick
          disableColumnMenu
          showToolbar
          slots={{ toolbar: SettingsToolbar }}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          pagination
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50, 100]}
          initialState={{
            columns: {
              columnVisibilityModel: { item_number: false },
            },
            density: 'compact',
          }}
          localeText={{
            noRowsLabel: isLoading ? '' : 'No Records To Display',
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: theme.palette.grey[100],
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontSize: '1rem',
              fontWeight: 600,
            },
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 0.5,
            },
            '& .MuiDataGrid-cell[data-field="min_quantity"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="max_quantity"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="dealer_mtd_sold"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="dealer_quantity_on_hand"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="auto_added"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="category"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="actions"]': {
              alignItems: 'center',
            },
            '& .MuiDataGrid-cell[data-field="description"]': {
              py: 0.75,
            },
          }}
        />
      </Box>

      <AddItemModal
        open={addItemModalOpen}
        onClose={handleAddItemModalClose}
        token={token}
        onItemsChanged={handleMinMaxItemsChanged}
      />
      <DeleteItemModal
        open={deleteItemModalOpen}
        onClose={handleDeleteItemModalClose}
        onDelete={() => deleteItem(itemToDelete)}
        loading={loading}
      />
      <RowDetailModal
        open={modalOpen}
        handleClose={handleItemCloseModal}
        itemNumber={selectedItemNumber}
      />

      <Dialog
        open={autoGenerateModalOpen}
        onClose={closeAutoGenerateModal}
        maxWidth='sm'
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${theme.palette.primary.main}2e`,
            boxShadow: `0 14px 34px ${theme.palette.primary.main}1f`,
            backgroundImage: `linear-gradient(180deg, ${theme.palette.primary.main}10 0%, ${theme.palette.background.paper} 34%)`,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.2,
            borderBottom: `1px solid ${theme.palette.primary.main}26`,
            backgroundColor: `${theme.palette.primary.main}12`,
          }}
        >
          <Typography variant='h6' sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
            Auto-Generate Min / Max
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Configure how many items to add and optionally filter by item type.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {autoGenerateFaqLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1 }}>
              <CircularProgress size={18} />
              <Typography sx={{ ml: 1 }}>Loading...</Typography>
            </Box>
          ) : autoGenerateFaqError ? (
            <Typography color='error'>{autoGenerateFaqError}</Typography>
          ) : (
            <Box sx={{ mb: autoGenerateStep === 'configure' ? 2 : 0.5 }}>
              {autoGenerateStep === 'configure' ? (
                <>
                  {autoGenerateFaq?.description && (
                    <Typography sx={{ fontWeight: 700, mb: 1.25 }}>
                      {autoGenerateFaq.description}
                    </Typography>
                  )}
                  {(() => {
                    const structured = parseStructuredFaqContent(
                      autoGenerateFaq?.detailed_description,
                    );

                    if (!structured) {
                      return (
                        <Typography sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                          {autoGenerateFaq?.detailed_description || ''}
                        </Typography>
                      );
                    }

                    const sections = Object.entries(structured);
                    const entries = sections.flatMap(([, section]) =>
                      Object.entries(section || {}),
                    );

                    return (
                      <Box
                        sx={{
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          backgroundColor: theme.palette.background.paper,
                        }}
                      >
                        <Box sx={{ px: 1.25, py: 0.75, backgroundColor: theme.palette.grey[100] }}>
                          <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                            Frequently Asked Questions
                          </Typography>
                        </Box>
                        <Box sx={{ px: 1.25, py: 0.5 }}>
                          {entries.map(([question, details], index) => (
                            <Box
                              key={`${question}-${index}`}
                              sx={{
                                py: 0.85,
                                borderBottom:
                                  index === entries.length - 1
                                    ? 'none'
                                    : `1px solid ${theme.palette.divider}`,
                              }}
                            >
                              <Typography variant='body2' sx={{ fontWeight: 700, mb: 0.25 }}>
                                {question}
                              </Typography>
                              <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                                {details?.description || ''}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })()}
                </>
              ) : (
                <Box
                  sx={{
                    minHeight: 96,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.warning.main}55`,
                      backgroundColor: `${theme.palette.warning.light}22`,
                      borderRadius: 1.5,
                      p: 1.5,
                    }}
                  >
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {`Right now your Min/Max list has ${autoGenerateCurrentCount} items. After you click Import, it will have ${autoGenerateProjectedCount} items.`}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}

          {autoGenerateStep === 'configure' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                select
                fullWidth
                size='small'
                label='How many items would you like to automatically add to your min / max settings?'
                value={autoGenerateCount}
                onChange={(event) => setAutoGenerateCount(event.target.value)}
              >
                <MenuItem value='5'>5</MenuItem>
                <MenuItem value='25'>25</MenuItem>
                <MenuItem value='50'>50</MenuItem>
                <MenuItem value='100'>100</MenuItem>
                <MenuItem value='250'>250</MenuItem>
                <MenuItem value='500'>500</MenuItem>
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='Item Type'
                value={autoGenerateItemType}
                onChange={(event) => setAutoGenerateItemType(event.target.value)}
              >
                {AUTO_GENERATE_ITEM_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: `1px solid ${theme.palette.primary.main}22`,
            backgroundColor: `${theme.palette.primary.main}08`,
          }}
        >
          <Button
            onClick={handleAutoGenerateCancel}
            sx={{ color: theme.palette.delete.main }}
            disabled={autoGenerateSubmitting}
          >
            {autoGenerateStep === 'confirm' ? 'Back' : 'Cancel'}
          </Button>
          <Button
            variant='contained'
            onClick={
              autoGenerateStep === 'configure' ? handleAutoGenerateNext : handleAutoGenerateConfirm
            }
            disabled={autoGenerateSubmitting}
          >
            {autoGenerateSubmitting ? (
              <CircularProgress size={18} sx={{ color: theme.palette.common.white }} />
            ) : autoGenerateStep === 'configure' ? (
              'Next'
            ) : (
              'Import'
            )}
          </Button>
        </DialogActions>
        {autoGenerateSubmitError && (
          <Box sx={{ px: 3, pb: 2 }}>
            <Typography color='error' variant='body2'>
              {autoGenerateSubmitError}
            </Typography>
          </Box>
        )}
      </Dialog>
    </Box>
  );
};

export default SettingsTable;
